<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'
import { useScroll, watchThrottled } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { useChapterTheme } from './composables/useChapterTheme'
import { useInfiniteChapter } from './composables/useInfiniteChapter'
import { useChapterVisibility } from './composables/useChapterVisibility'
import ScrollChapterItemDefault from './components/ScrollChapterItem.vue'
import ScrollChapterItemFlipnovel from './components/ScrollChapterItem.flipnovel.vue'
import ChapterThemeSettingsDefault from './components/ChapterThemeSettings.vue'
import ChapterThemeSettingsFlipnovel from './components/ChapterThemeSettings.flipnovel.vue'
import ChapterThemeSettingsNovelmello from './components/ChapterThemeSettings.novelmello.vue'
import ScrollLoadingIndicator from './components/ScrollLoadingIndicator.vue'
import type { ScrollChapter } from './composables/useInfiniteChapter'
import BizPopupBookTOCDefault from '@/components/BizPopupBookTOC.vue'
import BizPopupBookTOCFlipnovel from '@/components/BizPopupBookTOC.flipnovel.vue'
import ChapterBreadcrumb from '@/pages/chapter/components/ChapterBreadcrumb.vue'
import ChapterBreadcrumbNovelmello from '@/pages/chapter/components/ChapterBreadcrumb.novelmello.vue'
import ChapterLeadSentence from '@/pages/chapter/components/ChapterLeadSentence.vue'
import ChapterRecommend from '@/pages/chapter/components/ChapterRecommend.vue'
import ReaderTaboolaGuide from '@/pages/chapter/components/ReaderTaboolaGuide.vue'
import { CoinsSuccessPopup as ChapterCoinsSuccess, getChapterConfig, useSubscription } from '@/composables/payment'
import type { MemberTopupSubscribeProduct } from '@/composables/payment'
import { $formatErrorMessage } from '@/helpers'
import { api } from '@/api'
import { CHAPTER_LOCK, CHARGE_TYPE, ERROR_PAGE_LEVEL, VIP_TYPE } from '@/constants'
import { syncChapterListWithAudioStore, useAudioPlayerStore, useToastStore, useUserStore } from '@/stores'
import { useAxonSignUp } from '@/composables/axon/useAxonSignUp'
import { useBookshelf } from '@/composables/useBookshelf'
import { bannerShow, useBannerShow } from '@/composables/useBannerShow'
import { pageViewTrack, useBookRead, useChapterReadComplete, useElementClick } from '@/composables/tracks'
import { useBottomOverscroll } from '@/composables/useBottomOverscroll'
import { brand, theme as brandTheme, features } from '@/config/brands'
import { loadBrandComponent } from '@/utils/loadBrandConfig'
import { createDebugger } from '@/utils/debug'
import { useGptAdManager } from '@/pages/chapter-scroll/composables/useGptAdManager'
import { useFbqReadChapter } from '@/composables/fbq/useFbqReadChapter'
import { useFbqViewContent } from '@/composables/fbq/useFbqViewContent'
import { useFbqReadDuration } from '@/composables/fbq/useFbqReadDuration'
import { useReaderTaboolaGuide } from '@/pages/chapter/composables/useReaderTaboolaGuide'
import { notifyRetentionFloatingScroll, resetRetentionFloatingScrollState, setPendingLeaveNext, setRetentionActionHandler, useRetentionSubscriptionPopup } from '@/composables/useRetentionSubscriptionPopup'
import { useAgeGate } from '@/composables/useAgeGate'

const props = defineProps<{
  contentId: string
  chapterId: string
}>()
const enableRewardedVideoUnlock = features?.enableRewardedVideoUnlock === true
const enableRetentionSubscriptionPopup = features?.enableRetentionSubscriptionPopup === true
const retentionDebug = createDebugger('retention-popup')

// 加载当前品牌配置
const config = getChapterConfig()

const { checkAgeGate } = useAgeGate()

// 加载品牌专属主题设置弹窗
const ChapterThemeSettings = loadBrandComponent({
  rocnovel: ChapterThemeSettingsDefault,
  oceread: ChapterThemeSettingsDefault,
  talewave: ChapterThemeSettingsDefault,
  ficmoon: ChapterThemeSettingsDefault,
  novelarc: ChapterThemeSettingsDefault,
  storyella: ChapterThemeSettingsDefault,
  aurenovel: ChapterThemeSettingsDefault,
  silktale: ChapterThemeSettingsDefault,
  pearla: ChapterThemeSettingsDefault,
  fictora: ChapterThemeSettingsDefault,
  marinovel: ChapterThemeSettingsDefault,
  flipnovel: ChapterThemeSettingsFlipnovel,
  moonpage: ChapterThemeSettingsFlipnovel,
  novelune: ChapterThemeSettingsFlipnovel,
  storlyn: ChapterThemeSettingsFlipnovel,
  novelmello: ChapterThemeSettingsNovelmello,
})
// 显式将泛型钉为主站组件类型，保留主站 10 个品牌的 props 类型检查；
// flipnovel 家族变体 props/emits 形状不同，构建时按品牌只取其一，cast 安全。
const ScrollChapterItem = loadBrandComponent<typeof ScrollChapterItemDefault>({
  rocnovel: ScrollChapterItemDefault,
  oceread: ScrollChapterItemDefault,
  talewave: ScrollChapterItemDefault,
  ficmoon: ScrollChapterItemDefault,
  novelarc: ScrollChapterItemDefault,
  storyella: ScrollChapterItemDefault,
  aurenovel: ScrollChapterItemDefault,
  silktale: ScrollChapterItemDefault,
  pearla: ScrollChapterItemDefault,
  fictora: ScrollChapterItemDefault,
  marinovel: ScrollChapterItemDefault,
  flipnovel: ScrollChapterItemFlipnovel as unknown as typeof ScrollChapterItemDefault,
  moonpage: ScrollChapterItemFlipnovel as unknown as typeof ScrollChapterItemDefault,
  novelune: ScrollChapterItemFlipnovel as unknown as typeof ScrollChapterItemDefault,
  storlyn: ScrollChapterItemFlipnovel as unknown as typeof ScrollChapterItemDefault,
  novelmello: ScrollChapterItemDefault,
})
const ChapterTOCPopup = loadBrandComponent({
  rocnovel: BizPopupBookTOCDefault,
  oceread: BizPopupBookTOCDefault,
  talewave: BizPopupBookTOCDefault,
  ficmoon: BizPopupBookTOCDefault,
  novelarc: BizPopupBookTOCDefault,
  storyella: BizPopupBookTOCDefault,
  aurenovel: BizPopupBookTOCDefault,
  silktale: BizPopupBookTOCDefault,
  pearla: BizPopupBookTOCDefault,
  fictora: BizPopupBookTOCDefault,
  marinovel: BizPopupBookTOCDefault,
  flipnovel: BizPopupBookTOCFlipnovel,
  moonpage: BizPopupBookTOCFlipnovel,
  novelune: BizPopupBookTOCFlipnovel,
  storlyn: BizPopupBookTOCFlipnovel,
  novelmello: BizPopupBookTOCDefault,
})
// 顶栏:novelmello 用品牌变体(居中章节名 + 书架图标),其余品牌保持 default
const ChapterBreadcrumbBrand = loadBrandComponent({
  rocnovel: ChapterBreadcrumb,
  oceread: ChapterBreadcrumb,
  talewave: ChapterBreadcrumb,
  ficmoon: ChapterBreadcrumb,
  novelarc: ChapterBreadcrumb,
  storyella: ChapterBreadcrumb,
  aurenovel: ChapterBreadcrumb,
  silktale: ChapterBreadcrumb,
  pearla: ChapterBreadcrumb,
  fictora: ChapterBreadcrumb,
  marinovel: ChapterBreadcrumb,
  flipnovel: ChapterBreadcrumb,
  moonpage: ChapterBreadcrumb,
  storlyn: ChapterBreadcrumb,
  novaris: ChapterBreadcrumb,
  novelmello: ChapterBreadcrumbNovelmello,
})
const route = useRoute()
const router = useRouter()
const { showReaderTaboolaGuide } = useReaderTaboolaGuide(route)
const { $toast } = useToastStore()
const userStore = useUserStore()
const isFlipnovel = brand === 'flipnovel' || brand === 'moonpage' || brand === 'storlyn' || brand === 'novaris' || brand === 'novelune'
const { chapterVisitTrack, readCompleteTrackDirect } = useChapterReadComplete()
const { elementClick } = useElementClick()
const { start: startReadTime, bookRead } = useBookRead()
const isTalewave = brand === 'talewave'
const chapterScrollMenuIconColor = computed(() => {
  if (!isFlipnovel)
    return ''

  if (brand === 'flipnovel' || brand === 'novaris' || brand === 'novelune')
    return 'var(--rr-text-strong)'

  return brandTheme.textTertiary
})
const _chapterScrollMenuIconStyle = computed(() => {
  if (!isFlipnovel)
    return undefined

  return {
    color: chapterScrollMenuIconColor.value,
  }
})
const { reportReadChapter } = useFbqReadChapter()
const { reportViewContent } = useFbqViewContent()

const { startSession: startReadDurationSession } = useFbqReadDuration()
let readDurationSession: import('@/composables/fbq/useFbqReadDuration').ReadDurationSession | null = null

// 基础状态
const loading = ref(true)
const unlockChapterId = ref<string>('') // 用户点击解锁的章节 ID
const coinsSuccess = ref(false)
const audioStore = useAudioPlayerStore()
const book = ref<any>()
const discountPayInfo = ref<Record<string, any>>({
  discountOnceCoins: 0,
  bookImageUrl: '',
})
const containerRef = ref<HTMLElement>()
const chapterList = ref<any>([])
const landingList = ref<string[]>([])
const popupTOC = ref(false)

let firstPaidChapterNo: number | null = null
const popupTheme = ref(false)
const showCompleteRecommendLoading = ref(false)
const showRecommendChapterId = ref<string>('') // 需要显示推荐的章节 ID

const {
  popupVisible: retentionPopupVisible,
  selectedProduct: retentionProduct,
  shouldTriggerOnExit,
  shouldTriggerOnBalanceUpdate,
  shouldTriggerOnPaymentReturn,
  clearPendingPaymentCheck,
  showPopup: showRetentionPopup,
  restorePopup: restoreRetentionPopup,
} = useRetentionSubscriptionPopup()

// 仅开启挽留功能的品牌加载专用订阅档位，避免其他站点进入阅读页时新增商品接口请求。
const retentionSubscription = enableRetentionSubscriptionPopup
  ? useSubscription('chapter', {
    contentId: props.contentId,
    contentChapterId: props.chapterId,
  })
  : null
const retentionSubscriptionProducts = retentionSubscription?.currentRetentionSubscriptionProducts

if (retentionSubscriptionProducts) {
  watch(retentionSubscriptionProducts, (list) => {
    if (list?.length)
      retentionProduct.value = list[0]
    else
      retentionProduct.value = null
    tryShowRetentionPopupOnPaymentReturn()
  }, { immediate: true })
}

function syncRetentionProductFromLoadedList() {
  if (!retentionProduct.value && retentionSubscriptionProducts?.value?.length)
    retentionProduct.value = retentionSubscriptionProducts.value[0]

  return retentionProduct.value
}

function handleRetentionAction(product: MemberTopupSubscribeProduct | null) {
  const target = product || retentionProduct.value
  if (!target)
    return false

  return retentionSubscription?.onSubscribeProducts(target) ?? false
}
if (enableRetentionSubscriptionPopup)
  onUnmounted(setRetentionActionHandler(handleRetentionAction))

function tryShowRetentionPopup() {
  if (syncRetentionProductFromLoadedList()) {
    showRetentionPopup()
    return true
  }
  return false
}

function tryShowRetentionPopupOnPaymentReturn() {
  const isPaymentCancelReturn = route.query.payment_status === 'cancel'
  const canTrigger = isPaymentCancelReturn ? shouldTriggerOnPaymentReturn() : false
  const hasProduct = !!syncRetentionProductFromLoadedList()
  if (isPaymentCancelReturn && canTrigger && hasProduct) {
    restoreRetentionPopup()
    clearPendingPaymentCheck()
  }
}

let retentionBalanceChecking = false
async function tryShowRetentionPopupOnBalanceUpdate() {
  if (retentionBalanceChecking || !syncRetentionProductFromLoadedList() || retentionPopupVisible.value)
    return
  retentionBalanceChecking = true
  try {
    if (await shouldTriggerOnBalanceUpdate())
      tryShowRetentionPopup()
  }
  finally {
    retentionBalanceChecking = false
  }
}

// 加入书架（与书首页共享逻辑）
const { isInShelf, shelfLoading, toggle: onToggleShelf } = useBookshelf(book)

provide('containerRef', containerRef)
provide('chapterConfig', config)

// 使用主题管理 composable
const { theme, THEME_FONT, currentSkinBg } = useChapterTheme(config.themeDefaults)
provide('theme', theme)

const { SLOT_COUNT: adSlotCount } = useGptAdManager()

// 使用无限滚动章节加载 composable
const {
  chapters,
  loadingNext,
  loadingPrev,
  hasMoreNext,
  hasMorePrev,
  loadNextChapter,
  loadPrevChapter,
  unlockChapter,
  tryAutoUnlock,
  scrollToChapter,
  init: initChapters,
} = useInfiniteChapter({
  contentId: computed(() => props.contentId),
  initialChapterId: computed(() => props.chapterId),
  book,
  chapterList,
  containerRef,
})

// 埋点状态
const chapterReadState = ref<Map<string, {
  visitReported: boolean
  completeReported: boolean
  fbqReadReported: boolean
  enterTime: number
}>>(new Map())
const prevVisibleChapterId = ref<string>('')

// 章节进入可视区域
function onChapterEnter(chapter: ScrollChapter) {
  const state = chapterReadState.value.get(chapter.id) || {
    visitReported: false,
    completeReported: false,
    fbqReadReported: false,
    enterTime: 0,
  }

  state.enterTime = Date.now()

  if (!state.fbqReadReported && chapter.lockStatus !== CHAPTER_LOCK.LOCK) {
    reportReadChapter(props.contentId, chapter.id, chapter.sequenceNo, firstPaidChapterNo)
    state.fbqReadReported = true
  }

  // 上报章节访问（每章只上报一次，仅限未锁定章节）
  if (!state.visitReported && chapter.lockStatus !== CHAPTER_LOCK.LOCK) {
    chapterVisitTrack(props.contentId, chapter.id, chapter.sequenceNo, chapter.chapterVersionId)
    state.visitReported = true
  }

  chapterReadState.value.set(chapter.id, state)
}

// 章节离开可视区域
function onChapterLeave(chapter: ScrollChapter, direction: 'up' | 'down') {
  const state = chapterReadState.value.get(chapter.id)
  if (!state)
    return

  // 向上滚出表示该章节已读完
  if (direction === 'up' && !state.completeReported && chapter.lockStatus !== CHAPTER_LOCK.LOCK) {
    readCompleteTrackDirect(props.contentId, chapter.id, chapter.sequenceNo, chapter.chapterVersionId)
    state.completeReported = true
  }

  // 上报 biz_bookRead
  if (book.value) {
    bookRead({
      book_id: book.value.id,
      book_name: book.value.contentName,
      author: book.value?.authorInfo?.author,
      chapter_id: chapter.id,
      chapter_versionid: chapter.chapterVersionId,
      chapter_name: chapter.name,
      is_chargeable: chapter.feeType,
      serial_status: book.value.finishStatus === 0 ? 'ongoing' : 'completed',
      chapter_price: chapter.priceAmount,
      currency_coins: userStore.$state.coinBalance,
    })
  }
}

// 当前可见章节变化
function onCurrentChapterChange(newChapterId: string) {
  if (!prevVisibleChapterId.value) {
    prevVisibleChapterId.value = newChapterId
    return
  }

  // 找到新旧章节在列表中的位置
  const oldIndex = chapters.value.findIndex(c => c.id === prevVisibleChapterId.value)
  const newIndex = chapters.value.findIndex(c => c.id === newChapterId)

  if (oldIndex !== -1 && newIndex !== -1) {
    if (newIndex > oldIndex) {
      // 向下滚动到下一章
      elementClick('next_chapter')
    }
    else if (newIndex < oldIndex) {
      // 向上滚动到上一章
      elementClick('prev_chapter')
    }
  }

  // 章节切换时关闭主题设置弹窗
  popupTheme.value = false

  // 用户激活上报
  attributionTrack()

  prevVisibleChapterId.value = newChapterId
}

// 使用章节可见性检测 composable
const { currentChapterId, refresh: refreshVisibility, pause: pauseVisibility, resume: resumeVisibility, determineCurrentChapter } = useChapterVisibility({
  chapters,
  containerRef,
  contentId: computed(() => props.contentId),
  onChapterEnter,
  onChapterLeave,
  onCurrentChapterChange,
})

// 当前章节对象
const currentChapter = computed(() => {
  return chapters.value.find(c => c.id === currentChapterId.value)
})

const hasChapterAudio = computed(() => {
  if (audioStore.miniMode && audioStore.contentId === props.contentId)
    return false
  return !!currentChapter.value?.ttsAudioUrl
})

function onOpenAudioPlayer() {
  if (!currentChapter.value?.ttsAudioUrl)
    return
  audioStore.openPlayer({
    contentId: props.contentId,
    contentName: book.value?.contentName || '',
    contentCoverUrl: book.value?.contentCoverUrl || '',
    chapterId: currentChapterId.value || props.chapterId,
  })
}

// 只有滚动到（进入视口成为当前章）的锁定章节，余额足够时才自动扣费解锁；预加载章节不解锁
watch(currentChapterId, (id) => {
  if (id)
    tryAutoUnlock(id)
})

const isCurrentChapterLocked = computed(() =>
  currentChapter.value?.lockStatus === CHAPTER_LOCK.LOCK,
)

// 末章是否为锁定章（付费墙）：此时不算"读到全书最后一章"
const isLockedTail = computed(() =>
  chapters.value[chapters.value.length - 1]?.lockStatus === CHAPTER_LOCK.LOCK,
)

// 滚动相关
const { y, arrivedState } = useScroll(containerRef)

// 是否是最后一章（用于底部过度滚动跳推荐）；付费墙锁定章不触发
const isLastChapter = computed(() => !hasMoreNext.value && !isLockedTail.value)

// 底部过度滚动检测（用于跳转推荐页）
const { startListen: startOverscrollListen, stopListen: stopOverscrollListen } = useBottomOverscroll({
  containerRef,
  isAtBottom: computed(() => arrivedState.bottom),
  enabled: isLastChapter,
  threshold: 80,
  onTrigger: finishRecommend,
})

// 错误处理
function onErrorHandle() {
  router.replace({
    name: 'error',
    query: {
      level: ERROR_PAGE_LEVEL.DEFAULT,
      message: 'Network loading failure',
      redirect: window.encodeURIComponent(window.location.href),
    },
  })
}

/**
 * 获取书籍信息
 */
async function fetchBook() {
  const { data } = await api.get<any, GetContentGetcontentdetailResponse>('/content/getContentDetail', { params: { contentId: props.contentId } })
  book.value = data
  firstPaidChapterNo = (data as any).needPayChapterMinNo ?? null
  isInShelf.value = !!data.bookShelf
  checkAgeGate(data.ageGate)

  // 支付成功且类型是折扣充值则显示成功弹窗
  if (route.query.payment_status === 'success' && Number(route.query?.subscribeType) === VIP_TYPE.DISCOUNT && userStore.$state.landingPageId && userStore.$state.landingPageContentId === props.contentId) {
    coinsSuccess.value = true
    discountPayInfo.value.discountOnceCoins = route.query.discountOnceCoins
    discountPayInfo.value.bookImageUrl = data.contentCoverUrl
    pageViewTrack('sale')
  }
}

/**
 * 获取章节目录
 */
async function fetchChapterList() {
  try {
    const { data: chapterListData } = await api.get<any>('/content/getContentChapterBookList', {
      params: {
        contentId: props.contentId,
        pageIndex: 1,
        pageSize: 1500,
      },
    })
    chapterList.value = chapterListData.records
    // 同步听书播放器中已解锁章节状态，避免返回阅读页时目录显示锁定
    syncChapterListWithAudioStore(props.contentId, chapterList.value)
  }
  catch (error) {
    onErrorHandle()
  }
}

/**
 * 获取引导语句
 */
async function getLeadSentence() {
  const { data } = await api.get<any, GetContentGetLeadSentenceResponse>(`/content/getLeadSentence?contentId=${props.contentId}`)
  if (!data)
    return
  landingList.value = data?.split('\n')?.filter(Boolean) || []
}

/**
 * 获取最后的阅读位置，并替换路由为正确的 chapterId
 * 用于解决 book.firstChapter.id（加密格式）与章节列表 id（数字格式）不一致的问题
 */
async function fetchLatestPosition(): Promise<boolean> {
  const { data } = await api.post<any, PostReadPosResponse>('/read/pos', { contentId: book.value!.id })

  // 如果当前 chapterId 与服务器返回的不同，替换路由
  if (props.chapterId !== data.contentChapterId) {
    router.replace({
      name: 'chapter-scroll',
      params: { contentId: book.value!.id, chapterId: data.contentChapterId },
      query: route.query,
    })
    return true // 路由已替换，需要等待 watch 重新初始化
  }

  return false
}

/**
 * 用户激活上报
 */
async function attributionTrack() {
  if (localStorage.getItem('IsActive'))
    return

  try {
    const res = await api.get<any, any>('/member/active')
    if (res.code === 0) {
      localStorage.setItem('IsActive', '1')
      useAxonSignUp().reportSignUp()
    }
  }
  catch {
    // 静默失败
  }
}

/**
 * 页面关闭时的处理（beforeunload 事件）
 * 确保用户直接关闭页面时也能上报阅读数据
 */
function handleCloseEvent() {
  if (currentChapter.value && book.value) {
    bookRead({
      book_id: book.value.id,
      book_name: book.value.contentName,
      author: book.value?.authorInfo?.author,
      chapter_id: currentChapter.value.id,
      chapter_versionid: currentChapter.value.chapterVersionId,
      chapter_name: currentChapter.value.name,
      is_chargeable: currentChapter.value.feeType,
      serial_status: book.value.finishStatus === 0 ? 'ongoing' : 'completed',
      chapter_price: currentChapter.value.priceAmount,
      currency_coins: userStore.$state.coinBalance,
    })
  }
  return true
}

/**
 * 点击书名 → 跳转书首页
 */
function onClickBook() {
  router.push({ name: 'book', params: { contentId: props.contentId } })
}

/**
 * 打开目录
 */
function onOpenTOC() {
  popupTheme.value = false
  popupTOC.value = true
  attributionTrack()
}

/**
 * 目录章节点击检查
 * 如果章节已加载，直接滚动到该章节；否则允许路由跳转
 */
function handleTOCChapterClick(sequenceNo: number): boolean {
  const targetChapter = chapters.value.find(c => c.sequenceNo === sequenceNo)

  if (targetChapter) {
    // 章节已加载，直接滚动
    popupTOC.value = false

    if (targetChapter.sequenceNo === 1) {
      // 第一章，滚动到顶部显示导语
      if (containerRef.value)
        containerRef.value.scrollTop = 0
    }
    else {
      scrollToChapter(targetChapter.id)
    }

    return false // 阻止路由跳转
  }

  // 章节未加载，允许路由跳转重新初始化
  return true
}

/**
 * 上一章
 */
function onPrevChapter() {
  if (!currentChapter.value?.prevId)
    return

  // 关闭主题设置弹窗
  popupTheme.value = false

  elementClick('prev_chapter')

  // 检查上一章是否已加载
  const prevChapter = chapters.value.find(c => c.id === currentChapter.value!.prevId)
  if (prevChapter) {
    // 已加载，直接滚动到该章节
    scrollToChapter(prevChapter.id)
  }
  else {
    // 未加载，通过路由跳转重新初始化，保留现有 query 参数
    router.replace({ name: 'chapter-scroll', params: { contentId: props.contentId, chapterId: currentChapter.value.prevId }, query: route.query })
  }
}

/**
 * 下一章
 */
function onNextChapter() {
  // 关闭主题设置弹窗
  popupTheme.value = false

  // 如果 currentChapter 未设置（例如导语占满视口），默认使用第一个章节
  const chapter = currentChapter.value || chapters.value[0]

  // 用户激活上报
  attributionTrack()
  // 完读上报
  if (chapter && chapter.lockStatus !== CHAPTER_LOCK.LOCK)
    readCompleteTrackDirect(props.contentId, chapter.id, chapter.sequenceNo, chapter.chapterVersionId)

  elementClick('next_chapter')

  if (!chapter?.nextId) {
    // 无下一章，已是最后一章
    return
  }

  // 检查下一章是否已加载
  const nextChapter = chapters.value.find(c => c.id === chapter.nextId)
  if (nextChapter) {
    // 已加载，直接滚动到该章节
    scrollToChapter(nextChapter.id)
  }
  else {
    // 未加载，通过路由跳转重新初始化，保留现有 query 参数
    router.replace({ name: 'chapter-scroll', params: { contentId: props.contentId, chapterId: chapter.nextId }, query: route.query })
  }
}

/**
 * 切换主题设置
 */
function onToggleTheme() {
  popupTOC.value = false
  // 锁章页也允许点击切换：用于唤起/收起顶部导航栏；底部设置面板由 themeSettingsVisible 单独门控（锁章时不展示）
  popupTheme.value = !popupTheme.value
}

// 底部设置面板可见性：锁章页强制隐藏，仅非锁章且 popupTheme 时展示
const themeSettingsVisible = computed({
  get: () => popupTheme.value && !isCurrentChapterLocked.value,
  set: (val: boolean) => {
    popupTheme.value = val
  },
})

/**
 * 完读跳转推荐页
 */
function finishRecommend() {
  if (showCompleteRecommendLoading.value)
    return
  showCompleteRecommendLoading.value = true
  setTimeout(() => {
    router.push({
      name: 'recommend',
      params: { contentId: props.contentId },
      query: { status: book.value?.finishStatus === 0 ? 'ongoing' : 'finish' },
    })
  }, 800)
}

/**
 * 处理充值成功
 */
function handleCoinsSuccess() {
  coinsSuccess.value = false
  const url = new URL(window.location.href)
  url.searchParams.delete('subscribeType')
  url.searchParams.delete('discountOnceCoins')
  history.replaceState({}, '', url.href)
}

async function handlePaymentSuccess(chapterId?: string) {
  await userStore.init({ force: true })
  // 优先用支付前记录的实际付费章节(recommendChapterId)，避免整页回跳后 URL 路径滞后导致解锁错章
  const targetChapterId = chapterId || (route.query.recommendChapterId as string) || unlockChapterId.value || currentChapterId.value || props.chapterId
  // 仅当目标章节仍锁定时解锁,避免与 initChapters 内的自动解锁重复请求 /read/play(防止重复扣费)
  const target = chapters.value.find(c => c.id === targetChapterId)
  if (targetChapterId && target?.lockStatus === CHAPTER_LOCK.LOCK)
    await unlockChapter(targetChapterId)

  const url = new URL(window.location.href)
  url.searchParams.delete('payment_status')
  url.searchParams.delete('type')
  // 支付成功已解锁,清除 recommend 标记,避免后续 reload 误判展示推荐模块
  url.searchParams.delete('recommend')
  url.searchParams.delete('recommendChapterId')
  history.replaceState({}, '', url.href)

  clearPendingPaymentCheck()
}

// 与 Flutter 阅读页一致：finish 成功后重新拉取目录，并回到触发广告的锁章。
async function handleRewardSuccess(chapterId: string) {
  if (!enableRewardedVideoUnlock)
    return
  // 重建时会先插入目标章、再向上补前置章节；用加载态遮住该过程，
  // 避免用户看到目标章被第 7、8 章顶走后又滚回来的跳动。
  loading.value = true
  try {
    await fetchChapterList()
    await initChapters(chapterId)
    await userStore.init({ force: true })
  }
  finally {
    loading.value = false
  }

  await nextTick()
  scrollToChapter(chapterId)
  refreshVisibility()
}

/**
 * 支付回跳解锁:在 onMounted 章节初始化完成后串行调用(替代原 immediate watch,消除竞态导致的 /500 闪现)
 */
async function handlePaymentReturn() {
  if (isTalewave) {
    await handlePaymentSuccess()
  }
  else {
    await userStore.init({ force: true })
    // 优先用支付前记录的实际付费章节(recommendChapterId)，避免整页回跳后 URL 路径滞后导致解锁错章
    const targetChapterId = (route.query.recommendChapterId as string) || unlockChapterId.value || currentChapterId.value || props.chapterId
    // 仅当目标章节仍锁定时解锁,避免与 initChapters 内的自动解锁重复请求
    const target = chapters.value.find(c => c.id === targetChapterId)
    if (targetChapterId && target?.lockStatus === CHAPTER_LOCK.LOCK)
      await unlockChapter(targetChapterId)
    const url = new URL(window.location.href)
    url.searchParams.delete('payment_status')
    // 支付成功已解锁,清除 recommend 标记,避免后续 reload 误判展示推荐模块
    url.searchParams.delete('recommend')
    url.searchParams.delete('recommendChapterId')
    history.replaceState({}, '', url.href)
    clearPendingPaymentCheck()
  }
  unlockChapterId.value = ''
}

/**
 * 上报滚动位置
 */
async function reportScrollLocation() {
  if (!currentChapterId.value || !containerRef.value)
    return

  const chapter = chapters.value.find(c => c.id === currentChapterId.value)
  if (!chapter)
    return

  // 计算当前章节在容器中的相对滚动位置
  const chapterEl = containerRef.value.querySelector(`[data-chapter-id="${currentChapterId.value}"]`) as HTMLElement
  if (!chapterEl)
    return

  const containerScrollTop = containerRef.value.scrollTop
  const chapterTop = chapterEl.offsetTop
  const chapterHeight = chapterEl.offsetHeight
  const containerHeight = containerRef.value.clientHeight

  // 计算章节内的滚动位置
  const scrollInChapter = Math.max(0, containerScrollTop - chapterTop)
  const totalScrollable = Math.max(0, chapterHeight - containerHeight)

  try {
    await api.post('/read/report', {
      contentId: props.contentId,
      chapterId: currentChapterId.value,
      chapterVersionId: chapter.chapterVersionId,
      sectionMediaPlayedSeconds: Math.floor(scrollInChapter),
      videoDuration: totalScrollable,
    })
  }
  catch {
    // 静默失败
  }
}

// Mounted
onMounted(async () => {
  useGptAdManager().resetGptAdState()
  useGptAdManager().prepareInterstitial()
  readDurationSession = startReadDurationSession(props.contentId, props.chapterId)

  // 如果检测到页面是从"往返缓存"中读取的,刷新页面
  window.addEventListener('pageshow', (e) => {
    if (e.persisted)
      window.location.reload()
  })

  // 页面直接关闭时,需上报阅读事件
  window.addEventListener('beforeunload', handleCloseEvent)

  loading.value = true
  try {
    await initPage()
  }
  catch (error) {
    $toast($formatErrorMessage(error))
  }
  finally {
    loading.value = false
  }

  await afterInitPage()
  useBannerShow()

  // 从支付页面返回时，显示推荐
  if (route.query.recommend === '1' && !route.query.payment_status)
    showRecommendChapterId.value = route.query.recommendChapterId as string || ''

  const isPaymentCancelReturn = route.query.payment_status === 'cancel'
  if (enableRetentionSubscriptionPopup && isPaymentCancelReturn)
    tryShowRetentionPopupOnPaymentReturn()
  else
    clearPendingPaymentCheck()
})

/**
 * 初始化页面数据（用于 onMounted 与跨书切换时复用）
 */
async function initPage() {
  chapterReadState.value.clear()
  prevVisibleChapterId.value = ''
  getLeadSentence()
  // 两接口只依赖 URL 的 contentId,无数据依赖,并行发起;渲染仍等两者齐(initChapters 在其后)
  await Promise.all([fetchBook(), fetchChapterList()])
  reportViewContent(props.contentId, book.value?.contentName, 'chapter', props.chapterId)

  // 支付回跳:跳过"最新阅读位置"跳转,停留在用户付费的当前章节,避免并发导航导致 /500 闪现
  const isPaymentReturn = route.query.payment_status === 'success'
  // 如果没有 skipPosition 参数，先获取正确的 chapterId
  // 用于解决 book.firstChapter.id（加密格式）与章节列表 id（数字格式）不一致的问题
  if (!route.query.skipPosition && !isPaymentReturn) {
    const replaced = await fetchLatestPosition()
    if (replaced)
      return // 路由已替换，watch 会重新触发初始化
  }

  await initChapters()

  // 章节初始化完成后再处理支付回跳解锁(替代原 immediate watch,消除竞态)
  if (isPaymentReturn)
    await handlePaymentReturn()

  startReadTime.value = new Date().getTime()
}

/**
 * 初始化完成后滚动定位、刷新可见性检测与重新绑定 overscroll 监听
 */
async function afterInitPage() {
  // 等待 DOM 渲染后再滚动到初始章节并刷新可见性检测
  await nextTick()
  // 如果是第一章，保持在顶部显示导语；否则滚动到对应章节
  const initialChapter = chapters.value.find(c => c.id === props.chapterId)
  const audioProgress = Number(route.query.audioProgress)
  if (initialChapter && initialChapter.sequenceNo !== 1) {
    // 从听书页面跳转过来，定位到音频对应进度
    scrollToChapter(props.chapterId, audioProgress > 0 ? audioProgress : undefined)
  }

  // 清理 audioProgress 参数，避免刷新后重复定位
  if (audioProgress > 0) {
    const url = new URL(window.location.href)
    url.searchParams.delete('audioProgress')
    history.replaceState({}, '', url.href)
  }
  setTimeout(() => {
    refreshVisibility()
    // 初始化后立即计算当前可见章节，确保 currentChapterId 有值
    determineCurrentChapter()
  }, 200)

  stopOverscrollListen()
  // 启动底部过度滚动监听（需要在 DOM 渲染后）
  startOverscrollListen()
}

/**
 * 同书章节切换
 */
async function handleChapterChange(newChapterId: string) {
  useGptAdManager().resetGptAdState()
  if (!newChapterId || loading.value)
    return
  // 显示 loading（修复白屏问题）
  loading.value = true
  // 暂停可见性检测（修复跳转闪烁问题）
  pauseVisibility()

  // 关闭目录弹窗
  popupTOC.value = false

  // 清除埋点状态
  chapterReadState.value.clear()
  prevVisibleChapterId.value = ''

  try {
    // 重新初始化章节
    await initChapters()
  }
  finally {
    // 隐藏 loading
    loading.value = false
  }

  // 等待 DOM 渲染后再滚动到目标章节
  await nextTick()
  // 如果是第一章，保持在顶部显示导语；否则滚动到对应章节
  const targetChapter = chapters.value.find(c => c.id === newChapterId)
  if (targetChapter && targetChapter.sequenceNo !== 1)
    scrollToChapter(newChapterId)

  // 刷新可见性检测并恢复
  setTimeout(() => {
    refreshVisibility()
    // 恢复可见性检测（会自动调用 determineCurrentChapter）
    resumeVisibility()
    // 重新绑定底部过度滚动监听（因为 v-if 切换导致 DOM 重建）
    stopOverscrollListen()
    startOverscrollListen()
  }, 200)
}

// Watches
watch([() => props.contentId, () => props.chapterId], async ([newContentId, newChapterId], [oldContentId, oldChapterId]) => {
  // oldChapterId 允许为空:空 chapterId 进入时 fetchLatestPosition 会 replace 成正确章节,需要在此重新初始化
  if (!oldContentId)
    return
  if (newContentId !== oldContentId) {
    // 跨书切换：结束旧阅读时长会话并重新初始化页面
    useGptAdManager().resetGptAdState()
    readDurationSession?.endSession()
    readDurationSession = startReadDurationSession(newContentId, newChapterId)
    loading.value = true
    try {
      await initPage()
    }
    catch (error) {
      $toast($formatErrorMessage(error))
    }
    finally {
      loading.value = false
    }
    await afterInitPage()
  }
  else if (newChapterId !== oldChapterId) {
    // 同书章节切换
    await handleChapterChange(newChapterId)
  }
})

// 支付回跳自动解锁在 onMounted 章节初始化完成后串行处理(见 handlePaymentReturn),不再用 immediate watch,避免竞态导致 /500 闪现

// 支付成功后展示推荐流程（由 PaymentInlinePanel @success 触发）
function handlePaySuccess(chapterId: string) {
  const actualChapterId = chapterId || unlockChapterId.value || currentChapterId.value || props.chapterId
  if (!actualChapterId)
    return

  showRecommendChapterId.value = actualChapterId
  router.replace({
    path: `/content/${props.contentId}/scroll/${actualChapterId}`,
    query: { ...route.query, recommend: '1', recommendChapterId: actualChapterId },
  })
}

// 监听 recommend 参数变化
watch(() => route.query.recommend, (val) => {
  if (!val)
    showRecommendChapterId.value = ''
})

watch(() => userStore.$state.coinBalance, (balance, oldBalance) => {
  if (enableRetentionSubscriptionPopup && balance !== oldBalance)
    tryShowRetentionPopupOnBalanceUpdate()
})

/**
 * 推荐模块点击时保持 recommend 参数
 */
function appendRecommendParams() {
  const url = new URL(window.location.href)
  url.searchParams.set('recommend', '1')
  // 保留 recommendChapterId 参数
  if (showRecommendChapterId.value)
    url.searchParams.set('recommendChapterId', showRecommendChapterId.value)

  history.replaceState({}, '', url.href)
}

// 滚动监听
watchThrottled(y, async () => {
  // 上报滚动位置
  reportScrollLocation()

  // 更新当前可见章节（修复：滚动时更新章节）
  determineCurrentChapter()

  // 接近底部时加载下一章
  if (arrivedState.bottom && hasMoreNext.value)
    loadNextChapter()
  // 注：最后一章的推荐页跳转由 useBottomOverscroll 处理（需要用户继续向下拉才触发）

  // 接近顶部时加载上一章
  if (y.value < 100 && hasMorePrev.value)
    loadPrevChapter()
}, { throttle: 500, leading: true })

// Unmounted
onUnmounted(() => {
  useGptAdManager().destroyInterstitial()
  readDurationSession?.endSession()
  // 页面卸载时上报当前章节的阅读数据
  if (currentChapter.value && book.value) {
    bookRead({
      book_id: book.value.id,
      book_name: book.value.contentName,
      author: book.value?.authorInfo?.author,
      chapter_id: currentChapter.value.id,
      chapter_versionid: currentChapter.value.chapterVersionId,
      chapter_name: currentChapter.value.name,
      is_chargeable: currentChapter.value.feeType,
      serial_status: book.value.finishStatus === 0 ? 'ongoing' : 'completed',
      chapter_price: currentChapter.value.priceAmount,
      currency_coins: userStore.$state.coinBalance,
    })
  }

  window.removeEventListener('beforeunload', handleCloseEvent)
})

const leaveGuard = router.beforeEach((to, from, next) => {
  if (from.name !== 'chapter-scroll')
    return next()

  if (to.name === from.name)
    return next()

  const nextChapter = chapterList.value.find((item: any) => item.id === currentChapter.value?.nextId)
  const currentChapterIsPaid = currentChapter.value?.feeType === CHARGE_TYPE.CHAPTER
  const nextChapterIsPaid = nextChapter?.feeType === CHARGE_TYPE.CHAPTER
  const nextChapterPrice = book.value?.chargeType === CHARGE_TYPE.CHAPTER
    ? nextChapter?.chapterPrice
    : book.value?.priceAmount
  const balance = userStore.$state.coinBalance
  const canTriggerRetentionExit = enableRetentionSubscriptionPopup && shouldTriggerOnExit(balance, currentChapterIsPaid, nextChapterIsPaid, nextChapterPrice)
  if (canTriggerRetentionExit)
    syncRetentionProductFromLoadedList()
  retentionDebug('chapter-scroll leave guard %o', {
    enabled: enableRetentionSubscriptionPopup,
    from: from.fullPath,
    to: to.fullPath,
    chargeType: book.value?.chargeType,
    currentChapterId: currentChapter.value?.id,
    currentChapterFeeType: currentChapter.value?.feeType,
    currentChapterIsPaid,
    nextChapterId: currentChapter.value?.nextId,
    nextChapterFound: !!nextChapter,
    nextChapterFeeType: nextChapter?.feeType,
    nextChapterIsPaid,
    nextChapterPrice,
    balance,
    hasRetentionProduct: !!retentionProduct.value,
    canTriggerRetentionExit,
  })
  if (canTriggerRetentionExit) {
    if (!retentionProduct.value) {
      setPendingLeaveNext(null)
      next()
      return
    }
    if (!retentionPopupVisible.value)
      tryShowRetentionPopup()

    setPendingLeaveNext(() => {
      next()
    })
  }
  else {
    setPendingLeaveNext(null)
    next()
  }
})
onUnmounted(leaveGuard)
onUnmounted(resetRetentionFloatingScrollState)
</script>

<template>
  <div :class="`brand-${brand}`">
    <!-- 加载中 -->
    <div v-if="loading" class="min-h-100dvh flex justify-center items-center" :style="`background: ${theme.background}`">
      <BasicSpinner class="mx-auto" />
    </div>

    <!-- 主容器 -->
    <div v-else>
      <div
        ref="containerRef"
        :class="`h-100dvh overflow-scroll color-${theme.background.replace('#', '')}`"
        :style="`background: ${theme.background}; --theme-background: ${theme.background}`"
        @scroll.passive="enableRetentionSubscriptionPopup && notifyRetentionFloatingScroll()"
      >
        <!-- 皮肤背景图 -->
        <img
          v-if="currentSkinBg"
          :src="currentSkinBg"
          class="fixed top-0 right-0 w-252px h-310px pointer-events-none object-cover"
          :class="isFlipnovel ? 'rr-scroll-skin-bg' : 'z-10'"
          alt=""
        >
        <!-- 下载 App Banner -->
        <BizDownloadApp
          v-if="bannerShow && currentChapter"
          :id="contentId"
          position="fixed"
          class="fixed top-0 w-full"
          :index="currentChapter?.sequenceNo"
          from="chapter"
        />

        <!-- 导航栏（点击屏幕唤起/收起） -->
        <transition name="rr-topbar-slide">
          <div
            v-show="popupTheme"
            class="fixed w-full z-50"
            :class="{
              'top-60px': bannerShow,
              'top-0': !bannerShow,
            }"
            style="transform: translate(0)"
          >
            <ChapterBreadcrumbBrand
              :book-name="book?.contentName"
              :chapter-name="currentChapter?.name"
              :is-in-shelf="isInShelf"
              :shelf-loading="shelfLoading"
              @click-book="onClickBook"
              @toggle-shelf="onToggleShelf"
            />
          </div>
        </transition>

        <!-- 顶部加载指示器 -->
        <ScrollLoadingIndicator v-if="loadingPrev" position="top" />

        <!-- 章节列表 -->
        <div
          class="pt-10"
          :class="{ 'pt-24': bannerShow }"
          @click="onToggleTheme"
        >
          <!-- 引导语（仅在第一章前显示） -->
          <ChapterLeadSentence
            v-if="chapters.length > 0 && chapters[0].sequenceNo === 1"
            :landing-list="landingList"
            :sequence-no="1"
            :lead-sentence-style="THEME_FONT.leadSentence"
            class="mx-20px"
          />

          <div
            v-for="(chapter, index) in chapters"
            :key="chapter.id"
            :data-chapter-id="chapter.id"
          >
            <ScrollChapterItem
              :content-id="contentId"
              :chapter="chapter"
              :theme-font="THEME_FONT"
              :member-coin="userStore.$state.coinBalance"
              :is-last-chapter="!hasMoreNext && index === chapters.length - 1"
              :book-name="book?.contentName"
              :serial-status="book?.finishStatus"
              @pay-success="handlePaySuccess(chapter.id)"
              @reward-success="handleRewardSuccess(chapter.id)"
            />

            <!-- 推荐模块：在触发充值的章节后显示 -->
            <ChapterRecommend
              v-if="showRecommendChapterId === chapter.id"
              class="mx-20px mt-24px"
              :book-id="contentId"
              @read="appendRecommendParams"
              @collection="appendRecommendParams"
              @error="showRecommendChapterId = ''"
            />
          </div>
        </div>

        <!-- 底部加载指示器 -->
        <ScrollLoadingIndicator v-if="loadingNext" position="bottom" />

        <!-- 完读加载中 -->
        <div v-if="showCompleteRecommendLoading" class="fixed top-0 left-0 flex justify-center items-center w-full h-full z-100">
          <BasicSpinner />
        </div>

        <!-- 悬浮音频按钮 -->
        <div
          v-if="hasChapterAudio"
          class="fixed right-16px z-40 flex items-center justify-center w-44px h-44px rounded-full shadow-lg cursor-pointer"
          :class="{ 'opacity-50': !themeSettingsVisible && !popupTOC }"
          style="top: 50%; transform: translateY(-50%); background: var(--primary-color, #9875FB)"
          @click.stop="onOpenAudioPlayer"
        >
          <i-icon-park-outline-headset class="text-20px text-white" />
        </div>

        <!-- 主题设置弹窗 -->
        <ChapterThemeSettings
          v-model="themeSettingsVisible"
          :chapter="currentChapter"
          @prev-chapter="onPrevChapter"
          @next-chapter="onNextChapter"
          @open-toc="onOpenTOC"
        />

        <!-- 目录弹窗 -->
        <ChapterTOCPopup
          v-model="popupTOC"
          :book="book"
          :content-id="contentId"
          :chapter-id="currentChapterId || chapterId"
          route-name="chapter-scroll"
          :check-pass-goto-chapter="handleTOCChapterClick"
        />
      </div>

      <!-- 充值成功弹窗 -->
      <ChapterCoinsSuccess
        v-model="coinsSuccess"
        :discount-pay-info="discountPayInfo"
        @close="handleCoinsSuccess"
      />

      <ReaderTaboolaGuide v-model="showReaderTaboolaGuide" />
    </div>
  </div>
</template>

<style lang="scss">
@import './styles/chapter-theme.scss';
</style>

<style scoped lang="scss">
.rr-scroll-nav-open {
  background: var(--theme-background) !important;
  transition: background-color 0.22s ease, opacity 0.22s ease;
}

.rr-scroll-skin-bg {
  z-index: 1;
}

.rr-scroll-theme-topbar {
  min-height: 44px;
}

.rr-topbar-slide-enter-active,
.rr-topbar-slide-leave-active {
  transition: transform 0.28s ease, opacity 0.28s ease;
}

.rr-topbar-slide-enter,
.rr-topbar-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
